import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSiteViewModel } from '../ViewModel/SiteViewModel';

import axiosInstance from '../Core/RestHelper';
import { Trash2 } from 'lucide-react';

export const SitesListView: React.FC = () => {
  const viewModel = useSiteViewModel();
  const { sites, dashboardData, showAWSModal, setShowAWSModal, userData, fetchSites, isLoading } = viewModel;

  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ isOpen: boolean; deploymentId: string; appName: string }>({ isOpen: false, deploymentId: '', appName: '' });
  const [errorModal, setErrorModal] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });
  const [successModal, setSuccessModal] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });
  const navigate = useNavigate();

  const showDeleteConfirmation = (deploymentId: string, appName: string) =>
    setDeleteConfirmation({ isOpen: true, deploymentId, appName });
  const hideDeleteConfirmation = () =>
    setDeleteConfirmation({ isOpen: false, deploymentId: '', appName: '' });
  const showErrorModal = (title: string, message: string) =>
    setErrorModal({ isOpen: true, title, message });
  const hideErrorModal = () =>
    setErrorModal({ isOpen: false, title: '', message: '' });
  const hideSuccessModal = () => {
    setSuccessModal({ isOpen: false, title: '', message: '' });
    window.location.reload();
  };

  const handleDeleteDeployment = async () => {
    const { deploymentId } = deleteConfirmation;
    hideDeleteConfirmation();
    setDeletingIds(prev => new Set(prev).add(deploymentId));
    try {
      const response = await axiosInstance.delete(`deploy/deployments/${deploymentId}`);
      if (response.status === 200 || response.data.success) {
        window.location.reload();
      } else {
        showErrorModal('Delete Failed', response.data.message || 'Failed to delete deployment.');
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        showErrorModal('Not Found', error.response.data?.message || 'Deployment not found.');
      } else {
        showErrorModal('Delete Failed', error.response?.data?.message || 'Failed to delete deployment.');
      }
    } finally {
      setDeletingIds(prev => { const s = new Set(prev); s.delete(deploymentId); return s; });
    }
  };

  useEffect(() => { fetchSites(); }, [fetchSites]);
  useEffect(() => { dashboardData(); }, [dashboardData]);

  const statusStyle = (status: string) => {
    const s = status.toLowerCase();
    if (['active', 'completed', 'running'].includes(s)) return { color: '#22c55e', background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.2)' };
    if (['failed', 'error'].includes(s)) return { color: '#f43f5e', background: 'rgba(244,63,94,.1)', border: '1px solid rgba(244,63,94,.2)' };
    return { color: '#eab308', background: 'rgba(234,179,8,.1)', border: '1px solid rgba(234,179,8,.2)' };
  };

  return (
    <div style={{ background: '#09090e', minHeight: '100vh', fontFamily: "'Inter',sans-serif" }}>

      {/* Top bar */}
      <div style={{
        height: '50px', background: '#111116', borderBottom: '1px solid #26262f',
        display: 'flex', alignItems: 'center', padding: '0 20px', gap: '10px',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: '#8e8ea8' }}>
          <span>Home</span>
          <span style={{ color: '#52525f' }}>›</span>
          <span style={{ color: '#eeeef5', fontWeight: 500 }}>All Deployments</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <Link
            to="/deploy"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
              padding: '6px 12px', borderRadius: '6px', fontSize: '12.5px',
              fontWeight: 500, background: '#6366f1', color: '#fff',
              textDecoration: 'none', transition: 'background .15s',
            }}
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 11 11"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              style={{ flexShrink: 0, verticalAlign: 'middle' }}
            >
              <path d="M5.5 1v9M1 5.5h9"/>
            </svg>
            <span style={{ lineHeight: 1 }}>New Project</span>
          </Link>
        </div>
      </div>

      <div style={{ padding: '20px 24px' }}>

        {/* Page header */}
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontFamily: "'Syne',sans-serif", fontSize: '20px', fontWeight: 700, color: '#eeeef5', marginBottom: '3px' }}>
            Deployments
          </h1>
          <p style={{ fontSize: '13px', color: '#8e8ea8' }}>Monitor and manage all your application deployments</p>
        </div>

        {/* Stats strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '10px', marginBottom: '20px' }}>
          {[
            { label: 'Total', value: sites.length, sub: 'all time', color: '#eeeef5' },
            { label: 'Running', value: sites.filter(s => ['active','completed','running'].includes(s.status?.toLowerCase())).length, sub: '↑ Healthy', color: '#22c55e' },
            { label: 'Failed', value: sites.filter(s => ['failed','error'].includes(s.status?.toLowerCase())).length, sub: 'Needs fix', color: '#f43f5e' },
            { label: 'CI/CD Enabled', value: sites.filter(s => s.ci_cd).length, sub: 'auto-deploy on', color: '#818cf8' },
            { label: 'This Month', value: `${sites.length} sites`, sub: 'deployed', color: '#eeeef5' },
          ].map(stat => (
            <div key={stat.label} style={{
              background: '#111116', border: '1px solid #26262f',
              borderRadius: '10px', padding: '12px 14px',
            }}>
              <div style={{ fontSize: '10.5px', color: '#52525f', fontWeight: 500, marginBottom: '5px' }}>{stat.label}</div>
              <div style={{ fontSize: '20px', fontWeight: 700, fontFamily: "'Syne',sans-serif", color: stat.color, lineHeight: 1 }}>{stat.value}</div>
              <div style={{ fontSize: '10.5px', color: stat.color === '#eeeef5' ? '#52525f' : stat.color, marginTop: '3px' }}>{stat.sub}</div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '10px', overflow: 'hidden' }}>
          {isLoading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#52525f', fontSize: '13px' }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%',
                border: '2px solid #26262f', borderTopColor: '#6366f1',
                animation: 'spin 0.8s linear infinite', margin: '0 auto 12px',
              }}/>
              Loading deployments…
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : sites.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🚀</div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '15px', fontWeight: 700, color: '#eeeef5', marginBottom: '6px' }}>No deployments yet</div>
              <div style={{ fontSize: '13px', color: '#8e8ea8', marginBottom: '16px' }}>Deploy your first application to get started</div>
              <Link to="/deploy" style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                padding: '8px 16px', borderRadius: '6px', fontSize: '13px',
                fontWeight: 500, background: '#6366f1', color: '#fff', textDecoration: 'none',
              }}>
                + New Project
              </Link>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#17171e', borderBottom: '1px solid #26262f' }}>
                    {['App Name','Repository','Cloud','Service','CI/CD','Monitoring','Status','Branch','Deployed','Updated','Actions'].map(h => (
                      <th key={h} style={{
                        padding: '9px 14px', fontSize: '10.5px', fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: '.06em',
                        color: '#52525f', textAlign: 'left', whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sites.map(repo => (
                    <tr
                      key={repo.id}
                      style={{ borderBottom: '1px solid #26262f', transition: 'background .1s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#17171e')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={{ padding: '13px 14px' }}>
                        <Link to={`/sites/${repo.id}`} style={{
                          color: '#818cf8', fontWeight: 600, fontSize: '13px',
                          textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px',
                          whiteSpace: 'nowrap',
                        }}>
                          {repo.app_name}
                          <svg width="9" height="9" viewBox="0 0 9 9" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M1.5 7.5L7.5 1.5M4 1.5H7.5v3.5"/>
                          </svg>
                        </Link>
                      </td>
                      <td style={{ padding: '13px 14px', fontSize: '11.5px', color: '#8e8ea8', fontFamily: "'IBM Plex Mono',monospace", whiteSpace: 'nowrap' }}>
                        {repo.repo_name}
                      </td>
                      <td style={{ padding: '13px 14px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '3px',
                          fontSize: '10px', fontWeight: 700, padding: '2px 6px',
                          borderRadius: '3px', letterSpacing: '.03em',
                          background: 'rgba(255,153,0,.12)', color: '#ff9900',
                        }}>
                          {repo.selected_cloud || 'AWS'}
                        </span>
                      </td>
                      <td style={{ padding: '13px 14px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-flex', padding: '2px 7px', borderRadius: '20px',
                          fontSize: '10.5px', fontWeight: 600,
                          background: '#1e1e27', color: '#8e8ea8', border: '1px solid #26262f',
                        }}>
                          {repo.service_selected || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '13px 14px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-flex', padding: '2px 7px', borderRadius: '20px',
                          fontSize: '10.5px', fontWeight: 600,
                          background: repo.ci_cd ? 'rgba(99,102,241,.14)' : '#1e1e27',
                          color: repo.ci_cd ? '#818cf8' : '#52525f',
                          border: `1px solid ${repo.ci_cd ? 'rgba(99,102,241,.2)' : '#26262f'}`,
                        }}>
                          {repo.ci_cd ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td style={{ padding: '13px 14px', fontSize: '13px', color: repo.monitoring ? '#22c55e' : '#52525f', whiteSpace: 'nowrap' }}>
                        {repo.monitoring ? 'Yes' : '—'}
                      </td>
                      <td style={{ padding: '13px 14px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          padding: '2px 8px', borderRadius: '20px', fontSize: '10.5px', fontWeight: 600,
                          ...statusStyle(repo.status),
                        }}>
                          <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'currentColor', flexShrink: 0 }}/>
                          {repo.status}
                        </span>
                      </td>
                      <td style={{ padding: '13px 14px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', color: '#8e8ea8' }}>
                          {repo.branch}
                        </span>
                      </td>
                      <td style={{ padding: '13px 14px', fontSize: '12px', color: '#8e8ea8', whiteSpace: 'nowrap' }}>
                        {new Date(repo.created_on).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '13px 14px', fontSize: '12px', color: '#8e8ea8', whiteSpace: 'nowrap' }}>
                        {new Date(repo.modified_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '13px 14px', whiteSpace: 'nowrap' }}>
                        <button
                          onClick={() => showDeleteConfirmation(repo.id, repo.app_name)}
                          disabled={deletingIds.has(repo.id)}
                          title={`Delete ${repo.app_name}`}
                          style={{
                            width: '26px', height: '26px', borderRadius: '5px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: deletingIds.has(repo.id) ? 'not-allowed' : 'pointer',
                            color: '#52525f', border: '1px solid transparent',
                            background: 'transparent', transition: 'all .15s',
                          }}
                          onMouseEnter={e => {
                            (e.currentTarget as HTMLElement).style.background = 'rgba(244,63,94,.1)';
                            (e.currentTarget as HTMLElement).style.color = '#f43f5e';
                            (e.currentTarget as HTMLElement).style.borderColor = 'rgba(244,63,94,.3)';
                          }}
                          onMouseLeave={e => {
                            (e.currentTarget as HTMLElement).style.background = 'transparent';
                            (e.currentTarget as HTMLElement).style.color = '#52525f';
                            (e.currentTarget as HTMLElement).style.borderColor = 'transparent';
                          }}
                        >
                          {deletingIds.has(repo.id) ? (
                            <svg className="animate-spin" width="12" height="12" fill="none" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity=".25"/>
                              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                            </svg>
                          ) : (
                            <Trash2 size={12} />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: '#111116', border: '1px solid #32323d', borderRadius: '14px', padding: '24px', width: '420px', maxWidth: '95vw', boxShadow: '0 24px 60px rgba(0,0,0,.7)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(244,63,94,.1)', border: '1px solid rgba(244,63,94,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Trash2 size={16} color="#f43f5e" />
              </div>
              <div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '15px', fontWeight: 700, color: '#eeeef5' }}>Delete Deployment</div>
                <div style={{ fontSize: '12px', color: '#8e8ea8', marginTop: '2px' }}>This action cannot be undone</div>
              </div>
            </div>
            <div style={{ background: '#17171e', border: '1px solid #26262f', borderRadius: '6px', padding: '10px 12px', marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#8e8ea8', marginBottom: '3px' }}>App Name</div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#eeeef5' }}>{deleteConfirmation.appName}</div>
            </div>
            <p style={{ fontSize: '12px', color: '#f43f5e', marginBottom: '20px' }}>
              The deployment and all associated AWS resources will be permanently removed.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={hideDeleteConfirmation} style={{ padding: '7px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, background: '#17171e', border: '1px solid #32323d', color: '#eeeef5', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleDeleteDeployment} style={{ padding: '7px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, background: '#f43f5e', border: 'none', color: '#fff', cursor: 'pointer' }}>
                Delete Deployment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {successModal.isOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: '#111116', border: '1px solid #32323d', borderRadius: '14px', padding: '24px', width: '400px', maxWidth: '95vw' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(34,197,94,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#22c55e" strokeWidth="1.5"><path d="M2 8l5 5 7-7"/></svg>
              </div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '15px', fontWeight: 700, color: '#eeeef5' }}>{successModal.title}</div>
            </div>
            <p style={{ fontSize: '13px', color: '#8e8ea8', marginBottom: '20px' }}>{successModal.message}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={hideSuccessModal} style={{ padding: '7px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, background: '#22c55e', border: 'none', color: '#fff', cursor: 'pointer' }}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorModal.isOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={{ background: '#111116', border: '1px solid #32323d', borderRadius: '14px', padding: '24px', width: '400px', maxWidth: '95vw' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'rgba(244,63,94,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#f43f5e" strokeWidth="1.5"><circle cx="8" cy="8" r="6.5"/><path d="M8 5v3M8 10v.5"/></svg>
              </div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '15px', fontWeight: 700, color: '#eeeef5' }}>{errorModal.title}</div>
            </div>
            <p style={{ fontSize: '13px', color: '#8e8ea8', marginBottom: '20px' }}>{errorModal.message}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={hideErrorModal} style={{ padding: '7px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, background: '#f43f5e', border: 'none', color: '#fff', cursor: 'pointer' }}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* {!isAwsConnected &&
        <AWSSetupModal
          open={showAWSModal}
          onClose={() => setShowAWSModal(false)}
          onSuccess={() => setShowAWSModal(false)}
          externalId={userData?.external_id || ''}
        />
      } */}
    </div>
  );
};